/**
 * useBassButtonTones
 *
 * Lädt, was jeder Knopf einer Bass-Belegung klingt (`POST /api/bass-button-tones`) – für das
 * Anhören angeklickter Bassknöpfe (`BassBoard`). Die Ableitung bleibt im Backend, damit es keine
 * zweite Auslegung der Akkord-Figures gibt. vue-query mit `staleTime: Infinity` und dem JSON des
 * Layouts als Key: alle Boards mit derselben Belegung teilen eine Anfrage. `null` = keine Anfrage.
 */
import {computed, type MaybeRefOrGetter, toValue} from 'vue';
import {useQuery, useQueryClient} from '@tanstack/vue-query';

import {apiClient} from '@/api-client';
import type {BassButtonTonesResponse, BassLayoutSettings} from '@/generated';

export function useBassButtonTones(layout: MaybeRefOrGetter<BassLayoutSettings | null>) {
    const queryClient = useQueryClient();
    const query = useQuery(
        computed(() => {
            const body = toValue(layout);
            return {
                queryKey: ['bass-button-tones', body ? JSON.stringify(body) : 'idle'] as const,
                enabled: body !== null,
                queryFn: ({signal}: {signal: AbortSignal}) => apiClient.bassButtonTones(body!, {signal}),
                staleTime: Infinity,
                retry: false as const,
                refetchOnWindowFocus: false,
            };
        }),
        queryClient,
    );

    const tones = computed<BassButtonTonesResponse | null>(() => query.data.value ?? null);
    return {tones};
}
